import { useCallback, useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    google: any;
    initGoogleMapsBootstrap?: () => void;
  }
}

export interface PlaceSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY || "";

// Module-level promise so the bootstrap script loads only once across all callers.
let bootstrapPromise: Promise<void> | null = null;

const ensureGoogleMapsBootstrap = (): Promise<void> => {
  if (window.google?.maps?.importLibrary) return Promise.resolve();
  if (bootstrapPromise) return bootstrapPromise;

  bootstrapPromise = new Promise<void>((resolve, reject) => {
    // Script already injected — wait for it to finish initialising
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const poll = () => {
        if (window.google?.maps?.importLibrary) resolve();
        else setTimeout(poll, 100);
      };
      poll();
      return;
    }

    // First load — use the callback parameter so we know Maps is fully ready
    window.initGoogleMapsBootstrap = resolve;

    const script = document.createElement("script");
    script.src =
      `https://maps.googleapis.com/maps/api/js` +
      `?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}` +
      `&libraries=places&v=beta&loading=async` +
      `&callback=initGoogleMapsBootstrap`;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error("Failed to load Google Maps script"));
    document.head.appendChild(script);
  });

  return bootstrapPromise;
};

/** Loads a Google Maps JS API library (e.g. "places", "maps", "geocoding") once it's available. */
export const loadGoogleMapsLibrary = (name: string): Promise<any> =>
  ensureGoogleMapsBootstrap().then(() => window.google.maps.importLibrary(name));

// Module-level promise so the places library loads only once across all hook instances.
let placesLibPromise: Promise<any> | null = null;

const loadPlacesLibrary = (): Promise<any> => {
  if (!placesLibPromise) {
    placesLibPromise = loadGoogleMapsLibrary("places").then((lib) => {
      if (!lib?.AutocompleteSuggestion) throw new Error("AutocompleteSuggestion not available");
      return lib;
    });
  }
  return placesLibPromise;
};

export const useGooglePlacesSuggestions = () => {
  const [ready, setReady] = useState(false);
  const placesLibRef = useRef<any>(null);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) return;
    let cancelled = false;
    loadPlacesLibrary()
      .then((lib) => {
        if (cancelled) return;
        placesLibRef.current = lib;
        setReady(true);
      })
      .catch(() => {
        // API unavailable — field degrades to free-text input
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const searchPlaces = useCallback(async (query: string): Promise<PlaceSuggestion[]> => {
    if (!placesLibRef.current?.AutocompleteSuggestion || query.trim().length < 2) return [];
    try {
      const response = await placesLibRef.current.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        language: "en",
      });
      return ((response?.suggestions ?? []) as any[])
        .map((s) => {
          const pred = s.placePrediction;
          if (!pred) return null;
          return {
            placeId: pred.placeId ?? pred.place_id ?? "",
            mainText:
              pred.structuredFormat?.mainText?.text ??
              pred.structured_formatting?.main_text ??
              pred.text?.text ?? "",
            secondaryText:
              pred.structuredFormat?.secondaryText?.text ??
              pred.structured_formatting?.secondary_text ?? "",
          };
        })
        .filter((p): p is PlaceSuggestion => !!p && !!p.placeId && !!p.mainText);
    } catch {
      return [];
    }
  }, []);

  const fetchPlaceLocation = useCallback(async (placeId: string) => {
    if (!window.google?.maps?.places?.Place) return null;
    try {
      const place = new window.google.maps.places.Place({ id: placeId });
      const response = await place.fetchFields({
        fields: ["displayName", "formattedAddress", "location"],
      });
      const result = response?.place;
      if (!result) return null;
      const loc = result.location;
      return {
        name: result.displayName || result.formattedAddress || "",
        address: result.formattedAddress || "",
        latitude: typeof loc?.lat === "function" ? loc.lat() : loc?.lat,
        longitude: typeof loc?.lng === "function" ? loc.lng() : loc?.lng,
        placeId,
      };
    } catch {
      return null;
    }
  }, []);

  return { ready, searchPlaces, fetchPlaceLocation };
};
